import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { BEIGE_500, GRAY_900 } from 'constants/styling/theme'
import { DUPLICATE_DEAL_CANCELLATION_ERROR_CODE } from 'constants/deal'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'

import { DealDTOClient } from 'models/deal'
import { EmbedOrderActions } from 'components/pages/EmbedStatusCard/EmbedOrderActions.component'
import { EmbedOrderCancelled, EmbedOrderCancelledVariant } from 'components/pages/EmbedStatusCard/EmbedOrderCancelled.component'
import { EmbedPropertyError } from 'components/common/Embed/EmbedPropertyError/EmbedPropertyError'
import { OrderCancellationPopupBase } from 'components/common/OrderCancellationPopup/OrderCancellationPopup.component'
import { CancellationFeeAnalytics, NO_CANCELLATION_FEE_INFO, resolveCancellationFee } from 'components/common/OrderCancellationPopup/cancellationFee'
import { ProductKindsWithDocumentGallery } from 'constants/product'
import { PropertyOrderStatusStep } from 'models/property'
import { RescheduleOrderModal } from 'components/common/RescheduleOrderModal/RescheduleOrderModal.component'
import { RescheduleOrderModuleProvider } from 'components/common/RescheduleOrder'
import { earliestByShootingStart } from 'utils/time'
import { liveAssignments } from 'utils/stages'
import moment from 'moment-timezone'
import { logger } from 'utils/logging'
import { useBkbnSearchParams } from 'utils/hooks/useBkbnSearchParams'
import { useEmbedLocaleSelfHeal } from 'utils/hooks/useEmbedLocaleSelfHeal'
import { useGetOrder } from 'dataQueries/order.query'
import { useGetPropertyOrderStatuses, useGetPropertyReorderContextWithObjectReferenceId, usePropertyReorderContextResolver } from 'dataQueries/property.query'
import { SettledReorderContext, settleReorderContext } from 'utils/analytics/reorderClick'
import { useNavigateToEmbedPurchaseFlow } from 'components/pages/EmbedStatusCard/useNavigateToEmbedPurchaseFlow'
import { useOrderFlowLoaded } from 'components/pages/PurchaseFlow/_main/hooks/useOrderFlowLoaded'
import type { OrderFlowLoadFailureReason } from 'utils/analytics/orderFlowLoad'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/** The deal's earliest dated assignment, mirroring `GalleryDeal.context` (cancelled lines left out when `ignoreCancelledLines`). */
const earliestAssignmentFrom = (deal: DealDTOClient, ignoreCancelledLines: boolean) =>
  earliestByShootingStart(liveAssignments(deal.assignments, ignoreCancelledLines))

/**
 * Timezone of the deal's earliest assignment, mirroring `GalleryDeal.context`'s `earliestAssignment`.
 */
const earliestTimezoneFrom = (deal: DealDTOClient, ignoreCancelledLines: boolean): string | undefined =>
  earliestAssignmentFrom(deal, ignoreCancelledLines)?.timezone ?? undefined

/**
 * Shooting start fed to the cancel popup, derived like `GalleryDeal.context`: the earliest
 * assignment's start converted to that same assignment's timezone. Deriving both the start and the
 * timezone from the ONE earliest assignment (rather than pairing the status DTO's start with the
 * deal's timezone) keeps the embed's late-cancellation fee meter consistent with the Gallery.
 */
const cancelShootingStartFrom = (deal: DealDTOClient, ignoreCancelledLines: boolean): string | null => {
  const earliest = earliestAssignmentFrom(deal, ignoreCancelledLines)
  if (!earliest?.timezone || !earliest?.shootingStartDateTime) return null
  return moment(earliest.shootingStartDateTime).tz(earliest.timezone).toISOString()
}

/**
 * Whether the deal contains a document product, mirroring how `GalleryProduct.context` computes
 * `isDocumentProduct` (product kind in `ProductKindsWithDocumentGallery`), applied across all
 * assignment products of the deal.
 */
const isDocumentProductFrom = (deal: DealDTOClient): boolean =>
  deal.assignments?.some((assignment) => assignment.products.some((product) => ProductKindsWithDocumentGallery.has(product.kind))) ?? false

/**
 * Terminal error cause reported in the EMBED_STATUS_CARD_SHOWN funnel event; null while loading or
 * when an order (in any status, CANCELLED included) resolved. Every value here renders the
 * property error — a cancelled order is a healthy terminal state reported via statusStep instead,
 * so dashboards keyed on errorReason only count real failures.
 */
type EmbedOrderError = Extract<OrderFlowLoadFailureReason, 'MISSING_PARAMS' | 'STATUS_FETCH_FAILED' | 'ORDER_NOT_FOUND'>

/**
 * Controller for the Propstack embed order-actions surface (ISSUE-2018).
 *
 * Propstack renders the per-order status tiles in its own Medien grid and opens this page inside
 * its modal iframe with `?objectReferenceId=<crm id>&orderId=<orderPublicId>`. The page resolves
 * the order's status projection from the ISSUE-2015 by-property endpoint, the full deal via
 * `useGetOrder`, and renders the mock-faithful "Bestellung bearbeiten" content (recap + actions).
 *
 * An unknown orderId renders the property-error state. Cancelled orders render the dedicated
 * EmbedOrderCancelled screen instead (ISSUE-3147): right after an in-page cancellation as a success
 * confirmation (Propstack does not close its modal on its own, so the broker needs an in-iframe
 * terminal state), and on a deep link from a stale Propstack tile as an informational notice.
 */
export const EmbedStatusCardController: FC = () => {
  const { t } = useTranslation(['embed'])
  const ignoreCancelledLines = useFeatureFlag(FeatureFlag.ENABLE_SF_LAST_LINE_CANCEL)
  const params = useBkbnSearchParams()
  const objectReferenceId = params.get('objectReferenceId')
  const orderIdParam = params.get('orderId')
  // Strict digits-only parse: Number() would accept '1e3'/'0x2a'/' 42 ' from a malformed URL and
  // could coincidentally match a real orderId. A public param must be a plain integer or nothing.
  const orderId = orderIdParam && /^\d+$/.test(orderIdParam) ? Number(orderIdParam) : Number.NaN
  const hasValidParams = !!objectReferenceId && !Number.isNaN(orderId)
  const ids = useMemo(() => ({ orderId: Number.isNaN(orderId) ? null : orderId, objectReferenceId: objectReferenceId || null }), [orderId, objectReferenceId])
  const locale = params.get('lang')
  useEmbedLocaleSelfHeal(true, locale)

  const { userPropertyWorkspaceId } = useUserData()

  const statusesQuery = useGetPropertyOrderStatuses(objectReferenceId, userPropertyWorkspaceId || null)

  // Read ahead for the new-order click; the click settles it, so a slow read never holds the broker
  useGetPropertyReorderContextWithObjectReferenceId(objectReferenceId, userPropertyWorkspaceId || null)
  const resolveReorderContext = usePropertyReorderContextResolver()
  const reorderContext = useCallback((): SettledReorderContext => {
    if (!objectReferenceId || !userPropertyWorkspaceId) return null
    return settleReorderContext(resolveReorderContext({ workspaceId: userPropertyWorkspaceId, objectReferenceId }))
  }, [resolveReorderContext, objectReferenceId, userPropertyWorkspaceId])

  const navigateToPurchaseFlow = useNavigateToEmbedPurchaseFlow(objectReferenceId ?? '')
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false)
  const [isCancelOpen, setIsCancelOpen] = useState(false)
  // The cancel funnel: order, fee and clock are fixed when the dialog opens, so every event of that opening matches
  // the rendered notice. A close counts as abandoned only without a confirm in that opening; moving to another order
  // while the dialog is open abandons it.
  const cancelContextRef = useRef<typeof ids & { is_document_product: boolean } & CancellationFeeAnalytics>({
    ...ids, is_document_product: false, ...NO_CANCELLATION_FEE_INFO,
  })
  const cancelOpenedAtRef = useRef(moment())
  const cancelConfirmedRef = useRef(false)
  const isCancelOpenRef = useRef(false)
  isCancelOpenRef.current = isCancelOpen
  const previousOrderIdRef = useRef(orderId)
  useEffect(() => {
    if (previousOrderIdRef.current === orderId) return
    previousOrderIdRef.current = orderId
    if (isCancelOpenRef.current && !cancelConfirmedRef.current) logAnalyticsEvent(AnalyticsEvent.EMBED_STATUS_CARD_CANCEL_ABANDONED, cancelContextRef.current)
    setIsCancelOpen(false)
  }, [orderId])
  // Set once the in-page cancellation resolved (success, or a 409 meaning it was already cancelled
  // server-side); from then on the page renders the cancelled screen for THAT order regardless of
  // what the invalidated statuses query refetches. Keyed by orderId so a param-only navigation to
  // another order never inherits the screen.
  const [inPageCancellation, setInPageCancellation] = useState<{ orderId: number, variant: EmbedOrderCancelledVariant } | null>(null)

  const order = useMemo(
    () => (statusesQuery.data ?? []).find((status) => status.orderId === orderId) ?? null,
    [statusesQuery.data, orderId]
  )

  // Fetch the full deal once and share it between the actions page and the cancel popup.
  const isOrderResolved = !!order && order.statusStep !== PropertyOrderStatusStep.CANCELLED
  const orderQuery = useGetOrder(orderId, isOrderResolved)
  const deal = orderQuery.data?.data as DealDTOClient | undefined

  // Terminal error cause (null while loading or when an order resolved), used for the diagnostic
  // log + funnel event.
  const errorReason = useMemo<EmbedOrderError | null>(() => {
    if (!hasValidParams) return 'MISSING_PARAMS'
    if (!userPropertyWorkspaceId || statusesQuery.isPending) return null
    if (statusesQuery.isError) return 'STATUS_FETCH_FAILED'
    if (!order) return 'ORDER_NOT_FOUND'
    return null
  }, [hasValidParams, userPropertyWorkspaceId, statusesQuery.isPending, statusesQuery.isError, order])

  // order_flow_loaded with surface status_view (ISSUE-3218): the same launch handshake gates this card, so
  // "opened the placed card but the recap never rendered" is as visible as an order-flow load failure.
  useOrderFlowLoaded({
    surface: 'status_view',
    ready: !!order || !!errorReason,
    isEmbed: true,
    firstScreen: order ? 'order' : 'error',
    orderId: Number.isNaN(orderId) ? null : orderId,
    failureReason: order ? null : errorReason,
  })

  // Fire EMBED_STATUS_CARD_SHOWN once per mount on the first terminal state (order resolved in any
  // status, CANCELLED included, OR error), so EMBED_STATUS_CARD_ACTION_CLICKED has a complete
  // funnel denominator. A cancelled order reports its real statusStep — not an errorReason — so
  // dashboards keyed on errorReason only count actual failures. The error path also warns with the
  // distinguishing cause so a broker landing on EmbedPropertyError is diagnosable (bad params vs
  // status-fetch failure vs unknown order).
  const shownLoggedRef = useRef(false)
  useEffect(() => {
    if (shownLoggedRef.current) return
    if (order) {
      shownLoggedRef.current = true
      logAnalyticsEvent(AnalyticsEvent.EMBED_STATUS_CARD_SHOWN, { ...ids, statusStep: order.statusStep })
      return
    }
    if (errorReason) {
      shownLoggedRef.current = true
      logger.warn('Embed order-actions page could not render an order', undefined, {
        context: {
          component: 'EmbedStatusCardController',
          action: 'embedOrderActionsError',
          metadata: { errorReason, orderId: orderIdParam, objectReferenceId },
        },
      })
      logAnalyticsEvent(AnalyticsEvent.EMBED_STATUS_CARD_SHOWN, { ...ids, statusStep: null, errorReason })
    }
  }, [order, errorReason, orderIdParam, objectReferenceId, ids])

  // The deal fetch for a resolved order is a distinct failure surface (EmbedOrderActions renders an
  // inline error); log it here where the orderId is known so the blank recap is diagnosable.
  const dealErrorLoggedRef = useRef(false)
  useEffect(() => {
    if (dealErrorLoggedRef.current || !orderQuery.isError) return
    dealErrorLoggedRef.current = true
    logger.error('Embed order-actions deal fetch failed', orderQuery.error as Error | undefined, {
      context: {
        component: 'EmbedStatusCardController',
        action: 'embedOrderActionsDealFetch',
        metadata: { orderId: orderIdParam },
      },
    })
  }, [orderQuery.isError, orderQuery.error, orderIdParam])

  if (!hasValidParams) return <EmbedPropertyError />

  // The in-page cancellation outcome wins over whatever the invalidated statuses query refetches:
  // the order just turned CANCELLED on purpose, so the error states no longer apply.
  if (inPageCancellation?.orderId === orderId) return <EmbedOrderCancelled variant={inPageCancellation.variant} />

  if (statusesQuery.isPending || !userPropertyWorkspaceId) {
    return (
      <Stack alignItems='center' justifyContent='center' sx={{ height: '100%', p: 4 }} data-testid='embed-status-card-loading'>
        <CircularProgress />
      </Stack>
    )
  }

  if (statusesQuery.isError || !order) return <EmbedPropertyError />

  // Deep link to an order cancelled earlier (e.g. a not-yet-refreshed Propstack tile): an
  // informational notice, not the misleading property error.
  if (order.statusStep === PropertyOrderStatusStep.CANCELLED) return <EmbedOrderCancelled variant='ALREADY_CANCELLED' />

  return (
    <Stack sx={{ minHeight: '100%', backgroundColor: BEIGE_500 }}>

      <Box sx={{ px: 3, py: 2, backgroundColor: 'white', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant='text-2xl' sx={{ fontWeight: 700, color: GRAY_900 }}>{t('status_card.edit_order')}</Typography>
      </Box>

      <Box sx={{ p: 3, maxWidth: '960px', width: '100%', mx: 'auto' }}>
        <EmbedOrderActions
          order={order}
          objectReferenceId={objectReferenceId}
          deal={deal}
          isDealPending={orderQuery.isPending}
          isDealError={orderQuery.isError}
          onNewOrder={navigateToPurchaseFlow}
          reorderContext={reorderContext}
          onReschedule={() => setIsRescheduleOpen(true)}
          onCancel={() => {
            if (!deal) return
            const isDocumentProduct = isDocumentProductFrom(deal)
            cancelOpenedAtRef.current = moment()
            cancelContextRef.current = {
              ...ids,
              is_document_product: isDocumentProduct,
              ...resolveCancellationFee({
                shootingStart: cancelShootingStartFrom(deal, ignoreCancelledLines),
                earliestAssignmentTimezone: earliestTimezoneFrom(deal, ignoreCancelledLines),
                isDocumentProduct,
                now: cancelOpenedAtRef.current,
              }),
            }
            cancelConfirmedRef.current = false
            logAnalyticsEvent(AnalyticsEvent.EMBED_STATUS_CARD_CANCEL_OPENED, cancelContextRef.current)
            setIsCancelOpen(true)
          }}
        />
      </Box>

      {isRescheduleOpen && (
        <RescheduleOrderModuleProvider orderId={order.orderId} objectReferenceId={objectReferenceId}>
          <RescheduleOrderModal
            isOpen={true}
            onClose={() => setIsRescheduleOpen(false)}
            photographerContact={order.photographer}
          />
        </RescheduleOrderModuleProvider>
      )}
      {isCancelOpen && !!deal && (
        <OrderCancellationPopupBase
          isOpen={true}
          data={deal}
          dynamicNow={cancelOpenedAtRef.current}
          onClickClose={() => {
            if (!cancelConfirmedRef.current) logAnalyticsEvent(AnalyticsEvent.EMBED_STATUS_CARD_CANCEL_ABANDONED, cancelContextRef.current)
            setIsCancelOpen(false)
          }}
          onConfirm={(reason) => {
            cancelConfirmedRef.current = true
            logAnalyticsEvent(AnalyticsEvent.EMBED_STATUS_CARD_CANCEL_SUBMITTED, { ...cancelContextRef.current, cancellation_reason: reason })
          }}
          onCancelFailed={(httpStatus) => {
            logAnalyticsEvent(AnalyticsEvent.EMBED_STATUS_CARD_CANCEL_FAILED, { ...cancelContextRef.current, http_status: httpStatus })
          }}
          shootingStart={cancelShootingStartFrom(deal, ignoreCancelledLines)}
          earliestAssignmentTimezone={earliestTimezoneFrom(deal, ignoreCancelledLines)}
          isDocumentProduct={isDocumentProductFrom(deal)}
          onCancelSuccess={() => {
            logAnalyticsEvent(AnalyticsEvent.EMBED_STATUS_CARD_CANCEL_SUCCEEDED, { ...ids })
            setIsCancelOpen(false)
            setInPageCancellation({ orderId, variant: 'JUST_CANCELLED' })
          }}
          onAlreadyCancelled={() => {
            logAnalyticsEvent(AnalyticsEvent.EMBED_STATUS_CARD_CANCEL_FAILED, { ...cancelContextRef.current, http_status: DUPLICATE_DEAL_CANCELLATION_ERROR_CODE })
            setIsCancelOpen(false)
            setInPageCancellation({ orderId, variant: 'ALREADY_CANCELLED' })
          }}
        />
      )}
    </Stack>
  )
}

export default EmbedStatusCardController
